import {
  ButtonMenu,
  collapseColumn,
  column,
  Css,
  emptyCell,
  FilterDefs,
  GridDataRow,
  GridTableLayout,
  multiFilter,
  simpleHeader,
  useGridTableLayoutState,
} from "@homebound/beam";
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { NamedFragment } from "src/fragments/commonFragments.generated";
import { ProjectTypeFilter } from "src/generated/graphqlTypes";
import { createProjectUrl } from "src/RouteUrls";
import { useProjectContext } from "../context/ProjectContext";
import { createHierarchyBreadcrumbs } from "../ProjectHeader";
import { ProjectHeader_ProjectFragment } from "../ProjectHeader.generated";
import {
  HierarchyDashboardPage_ProjectFragment,
  useHierarchyDashboardPage_FilterOptionsQuery,
  useHierarchyDashboardPage_ProjectsQuery,
} from "./HierarchyDashboardPage.generated";

export function HierarchyDashboardPage() {
  const project = useProjectContext();
  // Fetch all children to populate filter options, since results can be paginated
  const filterOptionsQuery = useHierarchyDashboardPage_FilterOptionsQuery({ variables: { projectId: project.id! } });
  const filterDefs = useMemo(
    () => getFilterDefs(filterOptionsQuery.data?.project?.childrenRecursive ?? [], project.__typename),
    [filterOptionsQuery.data, project.__typename],
  );
  const layoutState = useGridTableLayoutState<HierarchyDashboardFilter>({
    persistedFilter: { filterDefs, storageKey: "hierarchy-dashboard-layout" },
    search: "server",
  });
  const parentRecursive = useMemo(() => {
    const filter = [...(layoutState.filter?.subdivision ?? []), ...(layoutState.filter?.community ?? [])];
    return filter.nonEmpty ? filter : [project.id!];
  }, [layoutState.filter, project.id]);
  // Get all structures associated with the selected parent projects
  const query = useHierarchyDashboardPage_ProjectsQuery({
    variables: {
      filter: { parentRecursive, type: [ProjectTypeFilter.Structure], search: layoutState.searchString },
      page: { offset: 0, limit: 1000 }, // TODO add pagination
    },
  });
  const structures = useMemo(() => query.data?.projectsPage.entities ?? [], [query.data?.projectsPage.entities]);
  const columns = useMemo(() => createColumns(structures), [structures]);

  return (
    <GridTableLayout
      pageTitle="Dashboard"
      breadcrumb={createHierarchyBreadcrumbs(project as ProjectHeader_ProjectFragment, createProjectUrl)}
      layoutState={layoutState}
      tableProps={{ columns, query, createRows: () => createRows(structures, project.__typename) }}
    />
  );
}

type HeaderRow = { kind: "header" };
type GroupRow = { kind: "group"; data: { name: string } };
type StructureRow = { kind: "structure"; id: string; data: HierarchyDashboardPage_ProjectFragment };
type Row = HeaderRow | GroupRow | StructureRow;

function createColumns(projects: HierarchyDashboardPage_ProjectFragment[]) {
  const designOptionGroups = projects
    .flatMap((project) => project.readyPlanConfig?.readyPlanOptions.map((rpo) => rpo.globalOption.group))
    .compact()
    .uniqueByKey("id");
  const projectColumn = column<Row>({
    id: "project",
    name: "project",
    header: "Project",
    structure: ({ id, name }) => <Link to={createProjectUrl(id)}>{name}</Link>,
    group: ({ name }) => ({ content: <div css={Css.smSb.$}>{name}</div>, colspan: 3 }),
  });
  const designOptionGroupColumns = designOptionGroups.map((og) =>
    column<Row>({
      id: og.id,
      name: og.name,
      header: og.name,
      structure: ({ readyPlanConfig }) =>
        readyPlanConfig?.readyPlanOptions.find((rpo) => rpo.globalOption.group.id === og.id)?.name ?? "",
      group: emptyCell,
    }),
  );
  const actionColumn = column<Row>({
    header: emptyCell,
    id: "action",
    name: "action",
    structure: ({ id, parent }) => (
      <ButtonMenu
        trigger={{ icon: "arrowFromLeft" }}
        items={[
          { label: "Go To Structure", onClick: createProjectUrl(id) },
          { label: "Go To Lot", onClick: createProjectUrl(parent!.id) },
        ]}
        contrast={false}
      />
    ),
    group: emptyCell,
    w: "50px",
  });

  return [collapseColumn<Row>(), projectColumn, ...designOptionGroupColumns, actionColumn];
}

function createRows(
  entities: HierarchyDashboardPage_ProjectFragment[],
  projectType: string | undefined,
): GridDataRow<Row>[] {
  // Build rows grouped by parent for each level
  function buildGroupRows(structures: HierarchyDashboardPage_ProjectFragment[], levels: string[]): GridDataRow<Row>[] {
    if (levels.isEmpty) return structures.map((s) => ({ kind: "structure" as const, id: s.id, data: s }));
    const [currentLevel, ...remainingLevels] = levels;
    // We can't use groupByObject with fragments, since Apollo doesn't guarantee stable identities.
    const grouped = structures.groupBy((s) => findParent(s, currentLevel)!.id);
    return grouped.toEntries().map(([parentId, structures]) => {
      const parent = findParent(structures.first!, currentLevel)!;
      return {
        kind: "group" as const,
        id: parentId,
        data: { name: parent.name },
        children: buildGroupRows(structures, remainingLevels),
      };
    });
  }
  const groupLevels = groupLevelsByType[projectType ?? ""] ?? [];
  return [simpleHeader, ...buildGroupRows(entities, groupLevels)];
}

const groupLevelsByType: Record<string, string[]> = {
  Market: ["Community", "Subdivision"],
  Community: ["Subdivision"],
};

type HierarchyDashboardFilter = { community?: string[]; subdivision?: string[] };

function getFilterDefs(filterOptions: NamedFragment[], projectType?: string): FilterDefs<HierarchyDashboardFilter> {
  return {
    ...(projectType === "Market" && {
      community: multiFilter({
        label: "Community",
        options: filterOptions.filter((p) => p.__typename === "Community").sortByKey("name"),
        getOptionValue: (o) => o.id,
        getOptionLabel: (o) => o.name,
      }),
    }),
    ...((projectType === "Market" || projectType === "Community") && {
      subdivision: multiFilter({
        label: "Subdivision",
        options: filterOptions.filter((p) => p.__typename === "Subdivision").sortByKey("name"),
        getOptionValue: (o) => o.id,
        getOptionLabel: (o) => o.name,
      }),
    }),
  };
}

function findParent(s: HierarchyDashboardPage_ProjectFragment, type: string) {
  return s.parentsRecursive.find((p) => p.__typename === type);
}
